import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

const PRESET_COLORS = [
  "#4F46E5", "#0E7B56", "#E11D48", "#1D5BBD", "#7C3AED",
  "#B45309", "#009688", "#0891B2", "#EA580C", "#65A30D",
  "#D97706", "#DB2777", "#2563EB", "#059669", "#7E22CE"
];

const generateRandomUniqueColor = (existingList = []) => {
  const usedColors = new Set(
    (existingList || []).map(t => (t.bg_color || t.color || "").toUpperCase().trim())
  );

  const available = PRESET_COLORS.filter(c => !usedColors.has(c.toUpperCase()));
  if (available.length > 0) {
    return available[Math.floor(Math.random() * available.length)];
  }

  let randomColor = "#";
  const letters = "0123456789ABCDEF";
  let count = 0;
  do {
    randomColor = "#";
    for (let i = 0; i < 6; i++) {
      randomColor += letters[Math.floor(Math.random() * 16)];
    }
    count++;
  } while (usedColors.has(randomColor.toUpperCase()) && count < 50);

  return randomColor;
};

const KPISettings = () => {
  const dispatch = useDispatch();
  const { taskTypes, monthlyTargets = [], loading } = useSelector((state) => state.kpi);

  const [activeSection, setActiveSection] = useState("config");
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [autoDeduct, setAutoDeduct] = useState(true);

  // Designations & Monthly Targets State
  const [designations, setDesignations] = useState([]);
  const [editingTargetScores, setEditingTargetScores] = useState({});
  const [savingTargetId, setSavingTargetId] = useState(null);
  const [targetError, setTargetError] = useState(null);

  // Add Target Score Modal State
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);
  const [selectedDesignationId, setSelectedDesignationId] = useState("");
  const [newTargetScore, setNewTargetScore] = useState("");
  const [savingNewTarget, setSavingNewTarget] = useState(false);
  const [addTargetError, setAddTargetError] = useState(null);

  // Task Type Modal State
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingTypeItem, setEditingTypeItem] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [typeScore, setTypeScore] = useState("");
  const [typeDelayScore, setTypeDelayScore] = useState("0");
  const [typeBgColor, setTypeBgColor] = useState("");
  const [creatingType, setCreatingType] = useState(false);
  const [typeError, setTypeError] = useState(null);

  // Delete Confirm Modal State
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingTypeItem, setDeletingTypeItem] = useState(null);
  const [deletingType, setDeletingType] = useState(false);

  useEffect(() => {
    dispatch.kpi.getTaskTypes();
    dispatch.kpi.getMonthlyTargets();

    const fetchDesignations = async () => {
      try {
        const res = await dispatch.projects.designationSearch({});
        if (res?.data && Array.isArray(res.data)) {
          setDesignations(res.data);
        }
      } catch (e) {
        console.error("Failed to fetch designations:", e);
      }
    };
    fetchDesignations();
  }, [dispatch]);

  // Target Score Handlers
  const handleTargetScoreChange = (id, val) => {
    setEditingTargetScores((prev) => ({
      ...prev,
      [id]: val,
    }));
  };

  const handleUpdateTargetScore = async (item) => {
    const id = item._id;
    const val = editingTargetScores[id];
    if (val === undefined || val === null || val === "") return;
    const numScore = Number(val);
    if (isNaN(numScore) || numScore < 0) {
      setTargetError("Monthly target score must be a non-negative number.");
      return;
    }

    setSavingTargetId(id);
    setTargetError(null);
    try {
      await dispatch.kpi.updateMonthlyTarget({
        id,
        payload: { monthly_target_score: numScore, is_active: true },
      });
      await dispatch.kpi.getMonthlyTargets();
      setEditingTargetScores((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err) {
      console.error("Failed to update target score:", err);
      setTargetError(err?.response?.data?.message || err?.message || "Failed to update target score");
    } finally {
      setSavingTargetId(null);
    }
  };

  const handleDeleteTargetScore = async (id) => {
    if (!window.confirm("Are you sure you want to remove this monthly target score?")) return;
    setSavingTargetId(id);
    setTargetError(null);
    try {
      await dispatch.kpi.deleteMonthlyTarget(id);
      await dispatch.kpi.getMonthlyTargets();
    } catch (err) {
      console.error("Failed to delete target score:", err);
      setTargetError(err?.response?.data?.message || err?.message || "Failed to delete target score");
    } finally {
      setSavingTargetId(null);
    }
  };

  const handleCreateTargetScore = async (e) => {
    e.preventDefault();
    if (!selectedDesignationId || newTargetScore === "" || savingNewTarget) return;
    const numScore = Number(newTargetScore);
    if (isNaN(numScore) || numScore < 0) {
      setAddTargetError("Monthly target score must be a non-negative number.");
      return;
    }

    setSavingNewTarget(true);
    setAddTargetError(null);
    try {
      await dispatch.kpi.createMonthlyTarget({
        designation: selectedDesignationId,
        monthly_target_score: numScore,
        is_active: true,
      });
      await dispatch.kpi.getMonthlyTargets();
      setShowAddTargetModal(false);
      setSelectedDesignationId("");
      setNewTargetScore("");
    } catch (err) {
      console.error("Failed to create target score:", err);
      setAddTargetError(err?.response?.data?.message || err?.message || "Failed to create target score");
    } finally {
      setSavingNewTarget(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingTypeItem(null);
    setTypeName("");
    setTypeScore("");
    setTypeDelayScore("0");
    setTypeBgColor(generateRandomUniqueColor(taskTypes));
    setTypeError(null);
    setShowTypeModal(true);
  };

  const handleOpenEditModal = (t) => {
    setEditingTypeItem(t);
    setTypeName(t.name || t.kpi_type_name || "");
    setTypeScore(t.score !== undefined ? String(t.score) : "0");
    setTypeDelayScore(t.task_delay_score !== undefined ? String(t.task_delay_score) : "0");
    setTypeBgColor(t.bg_color || generateRandomUniqueColor(taskTypes));
    setTypeError(null);
    setShowTypeModal(true);
  };

  const handleSaveTaskType = async (e) => {
    e.preventDefault();
    if (!typeName.trim() || typeScore === "" || creatingType) return;
    if (Number(typeScore) < 0) {
      setTypeError("Score must be at least 0");
      return;
    }

    setCreatingType(true);
    setTypeError(null);
    try {
      const payload = {
        name: typeName.trim(),
        kpi_type_name: typeName.trim(),
        score: Number(typeScore),
        task_delay_score: Number(typeDelayScore || 0),
        bg_color: typeBgColor || "#4F46E5",
        is_active: true
      };

      if (editingTypeItem && (editingTypeItem._id || editingTypeItem.id)) {
        const typeId = editingTypeItem._id || editingTypeItem.id;
        await dispatch.kpi.updateTaskType({ id: typeId, payload });
      } else {
        await dispatch.kpi.createTaskType(payload);
      }

      setShowTypeModal(false);
      setEditingTypeItem(null);
      setTypeName("");
      setTypeScore("");
      setTypeDelayScore("");
      setTypeBgColor("");
      dispatch.kpi.getTaskTypes();
    } catch (err) {
      console.error("Failed to save task type", err);
      setTypeError(err?.response?.data?.message || err?.message || "Failed to save work type");
    } finally {
      setCreatingType(false);
    }
  };

  const handleOpenDeleteModal = (t) => {
    setDeletingTypeItem(t);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDeleteTaskType = async () => {
    if (!deletingTypeItem) return;
    const typeId = deletingTypeItem._id || deletingTypeItem.id;
    setDeletingType(true);
    try {
      await dispatch.kpi.deleteTaskType(typeId);
      setShowDeleteConfirmModal(false);
      setDeletingTypeItem(null);
      dispatch.kpi.getTaskTypes();
    } catch (err) {
      console.error("Failed to delete task type", err);
      alert(err?.response?.data?.message || err?.message || "Failed to delete task type");
    } finally {
      setDeletingType(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Settings</h1>
        <p className="text-slate-500 text-sm">
          Configure KPI scoring rules, categories, integrations and access
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 shrink-0">
          <nav className="flex flex-col space-y-1">
            <button 
              onClick={() => setActiveSection("config")}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === 'config' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
              KPI configuration
            </button>
            <button 
              onClick={() => setActiveSection("types")}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${activeSection === 'types' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
              Work types & scores
            </button>
          </nav>
        </div>

        {/* Main Configuration Area */}
        <div className="flex-1 space-y-6">
          {activeSection === "types" ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Work Types & KPI Points</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Manage available task types, points, delay penalties, and color coding</p>
                </div>
                <button 
                  onClick={handleOpenAddModal}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors flex items-center gap-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  Add Work Type
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Default Points</th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Delay Penalty</th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(taskTypes || []).length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-sm text-slate-400 italic">
                          No work types configured yet.
                        </td>
                      </tr>
                    ) : (
                      (taskTypes || []).map((t) => {
                        const hexColor = t.bg_color || "#4F46E5";
                        return (
                          <tr key={t._id || t.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2.5">
                                <span className="w-3.5 h-3.5 rounded-full shrink-0 shadow-2xs border border-black/10" style={{ backgroundColor: hexColor }}></span>
                                <span className="text-sm font-semibold text-slate-900">{t.name || t.kpi_type_name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-slate-900">+{t.score || 0}</td>
                            <td className="px-6 py-4 text-sm font-semibold text-rose-600">-{t.task_delay_score || 0}</td>
                            <td className="px-6 py-4 text-right space-x-2">
                              <button 
                                onClick={() => handleOpenEditModal(t)}
                                className="px-2.5 py-1 text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                                title="Edit Work Type"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleOpenDeleteModal(t)}
                                className="px-2.5 py-1 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
                                title="Delete Work Type"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* Scoring Rules Card - Commented out as requested
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-100">
                  <h2 className="text-base font-semibold text-slate-900">KPI scoring rules</h2>
                  <p className="text-sm text-slate-500 mt-0.5">How points are calculated and reviewed</p>
                </div>

                <div className="divide-y divide-slate-100">
                  <div className="px-6 py-5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Cycle</div>
                      <div className="text-sm text-slate-500 mt-0.5">When KPI scores roll over and reports auto-generate</div>
                    </div>
                    <span className="text-xs font-semibold px-3 py-1.5 border border-slate-200 rounded-lg text-slate-700 bg-slate-50">
                      Monthly - 1st of each month
                    </span>
                  </div>

                  <div className="px-6 py-5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Require review submission to award points</div>
                      <div className="text-sm text-slate-500 mt-0.5">Points are calculated automatically upon transitioning work to Review</div>
                    </div>
                    <button 
                      onClick={() => setApprovalRequired(!approvalRequired)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${approvalRequired ? 'bg-slate-900' : 'bg-slate-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${approvalRequired ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  <div className="px-6 py-5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">Auto-deduct on late submission</div>
                      <div className="text-sm text-slate-500 mt-0.5">Deduct points automatically if submitted after task end date</div>
                    </div>
                    <button 
                      onClick={() => setAutoDeduct(!autoDeduct)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoDeduct ? 'bg-slate-900' : 'bg-slate-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoDeduct ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
              */}

              {/* Target Scores Card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">Target scores</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Monthly target by role</p>
                  </div>
                  <button
                    onClick={() => {
                      setAddTargetError(null);
                      setSelectedDesignationId("");
                      setNewTargetScore("");
                      setShowAddTargetModal(true);
                    }}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-2xs"
                  >
                    <span>+ Set Role Target</span>
                  </button>
                </div>

                {targetError && (
                  <div className="mx-6 mt-4 p-3 bg-rose-50 text-rose-700 text-xs font-semibold rounded-lg border border-rose-200">
                    {targetError}
                  </div>
                )}
                
                <div className="px-6 py-2 divide-y divide-slate-100">
                  {monthlyTargets.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-400 font-medium">
                      No monthly target scores configured yet. Click "+ Set Role Target" to add target scores by role.
                    </div>
                  ) : (
                    monthlyTargets.map((item, idx) => {
                        const targetId = item._id;
                        const roleName =
                          (typeof item.designation === "object"
                            ? item.designation?.name || item.designation?.designation_name
                            : null) || "Role";

                        const currentVal =
                          editingTargetScores[targetId] !== undefined
                            ? editingTargetScores[targetId]
                            : item.monthly_target_score;

                        const isChanged =
                          editingTargetScores[targetId] !== undefined &&
                          String(editingTargetScores[targetId]) !== String(item.monthly_target_score);

                        const isSaving = savingTargetId === targetId;

                        return (
                          <div key={targetId || idx} className="flex items-center justify-between py-3.5 px-2 hover:bg-slate-50/50 rounded-xl transition-colors">
                            <div className="text-sm font-bold text-slate-900 truncate pr-4" title={roleName}>
                              {roleName}
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="relative flex items-center">
                                <input 
                                  type="number" 
                                  min="0"
                                  value={currentVal} 
                                  onChange={(e) => handleTargetScoreChange(targetId, e.target.value)}
                                  className="w-28 pl-3 pr-10 py-1.5 text-right text-sm font-bold border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 bg-white text-slate-900 shadow-2xs"
                                />
                                <span className="absolute right-3 text-xs font-semibold text-slate-400 pointer-events-none">pts</span>
                              </div>

                              {isChanged && (
                                <button
                                  type="button"
                                  onClick={() => handleUpdateTargetScore(item)}
                                  disabled={isSaving}
                                  className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-2xs cursor-pointer"
                                >
                                  {isSaving ? "Saving..." : "Save"}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => handleDeleteTargetScore(targetId)}
                                disabled={isSaving}
                                title="Remove target"
                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                              </button>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Work Type Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-slate-900">
                {editingTypeItem ? "Edit Work Type" : "Create Work Type"}
              </h3>
              <button 
                onClick={() => setShowTypeModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            {typeError && (
              <div className="mb-4 p-3 bg-rose-50 text-rose-700 text-xs font-semibold rounded-lg border border-rose-200">
                {typeError}
              </div>
            )}

            <form onSubmit={handleSaveTaskType} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Work Type Name <span className="text-rose-500">*</span></label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. Code Review"
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Default KPI Points <span className="text-rose-500">*</span></label>
                  <input 
                    type="number"
                    required
                    min="0"
                    placeholder="e.g. 75"
                    value={typeScore}
                    onChange={(e) => setTypeScore(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Delayed Penalty Points</label>
                  <input 
                    type="number"
                    min="0"
                    placeholder="e.g. 10"
                    value={typeDelayScore}
                    onChange={(e) => setTypeDelayScore(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Background Color (Unique Hex)</label>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-9 h-9 rounded-lg border border-slate-200 shrink-0 shadow-2xs" 
                    style={{ backgroundColor: typeBgColor || "#4F46E5" }}
                  ></div>
                  <input 
                    type="text"
                    required
                    placeholder="#4F46E5"
                    value={typeBgColor}
                    onChange={(e) => setTypeBgColor(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 uppercase"
                  />
                  <button
                    type="button"
                    onClick={() => setTypeBgColor(generateRandomUniqueColor(taskTypes))}
                    className="px-3 py-2 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shrink-0"
                    title="Generate Random Unique Color"
                  >
                    🎲 Random
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowTypeModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingType || !typeName.trim() || typeScore === ""}
                  className="px-4 py-2 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50"
                >
                  {creatingType ? "Saving..." : (editingTypeItem ? "Update Work Type" : "Save Work Type")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 text-center">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">Delete Work Type?</h3>
            <p className="text-xs text-slate-500 mb-6">
              Are you sure you want to delete <span className="font-semibold text-slate-800">"{deletingTypeItem?.name || deletingTypeItem?.kpi_type_name}"</span>? This will deactivate the work type.
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg border border-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteTaskType}
                disabled={deletingType}
                className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
              >
                {deletingType ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Role Target Modal */}
      {showAddTargetModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-slate-900">Set Role Target Score</h3>
              <button 
                onClick={() => setShowAddTargetModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            {addTargetError && (
              <div className="mb-4 p-3 bg-rose-50 text-rose-700 text-xs font-semibold rounded-lg border border-rose-200">
                {addTargetError}
              </div>
            )}

            <form onSubmit={handleCreateTargetScore} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Select Designation / Role <span className="text-rose-500">*</span>
                </label>
                <Select
                  options={designations.map((d) => {
                    const dId = d._id || d.id;
                    const dName = d.name || d.designation_name || "Role";
                    const hasTarget = monthlyTargets.some(
                      (t) => (typeof t.designation === "object" ? t.designation?._id : t.designation) === dId
                    );
                    return {
                      value: dId,
                      label: `${dName}${hasTarget ? " (Target set)" : ""}`,
                      isDisabled: hasTarget
                    };
                  })}
                  className="text-sm text-slate-900"
                  classNamePrefix="select"
                  placeholder="Select a role..."
                  onChange={(opt) => setSelectedDesignationId(opt ? opt.value : "")}
                  value={designations
                    .map((d) => {
                      const dId = d._id || d.id;
                      const dName = d.name || d.designation_name || "Role";
                      const hasTarget = monthlyTargets.some(
                        (t) => (typeof t.designation === "object" ? t.designation?._id : t.designation) === dId
                      );
                      return { value: dId, label: `${dName}${hasTarget ? " (Target set)" : ""}` };
                    })
                    .find((opt) => opt.value === selectedDesignationId) || null}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#e2e8f0',
                      borderRadius: '0.5rem',
                      padding: '1px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#0f172a'
                      }
                    })
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Monthly Target Score <span className="text-rose-500">*</span>
                </label>
                <input 
                  type="number"
                  required
                  min="0"
                  placeholder="e.g. 800"
                  value={newTargetScore}
                  onChange={(e) => setNewTargetScore(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingNewTarget}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-2xs"
                >
                  {savingNewTarget ? "Saving..." : "Save Target"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPISettings;
